import { getURLSearchParamsFromQuery } from '@schibsted-nmp/search-frontend-clients';
import type { SearchResult } from '@schibsted-nmp/search-frontend-clients/quest';
import deepEqual from '@schibsted-nmp/search-frontend-utils/deepEqual.js';
import deepMerge from '@schibsted-nmp/search-frontend-utils/deepMerge.js';
import {
    useQuery,
    useQueryClient,
    type QueryClient,
    type QueryFunctionContext,
} from '@tanstack/react-query';
import React from 'react';
import { useAppConfig } from '../AppConfig';
import { useQueryErrorBoundary } from './QueryErrorBoundary';
import { useSearchParams } from './SearchParamsStore';
import { getSearchQueryKey } from './SearchQuery';
// import { APP_URL_PARAMS } from '../state/UrlState';

interface SearchQueryMeta {
    apiUrl: string;
    queryClient: QueryClient;
}

declare module '@tanstack/react-query' {
    // @ts-expect-error Duplicate identifier 'QueryMeta'.ts(2300)
    type QueryMeta = SearchQueryMeta;
}

export function useSearchData() {
    const { apiUrl, searchKey } = useAppConfig();
    const { searchParams, setSearchParams } = useSearchParams();

    const queryClient = useQueryClient();

    const searchResult = useQuery({
        ...getQueryCacheOptions(),
        meta: {
            apiUrl,
            queryClient,
        },
        // // @ts-expect-error Because of reasons
        // structuralSharing: mergeWithPreviousFilters,
        queryFn: fetchSearchData,
        queryKey: getSearchQueryKey({ searchKey, searchParams }),
    });

    React.useEffect(
        function updateStoreWithParamsFromSearchResult() {
            if (searchResult.data) {
                setSearchParams(searchResult.data.metadata.params);
            }
        },
        [searchResult.data, setSearchParams],
    );

    return {
        error: searchResult.error,
        isError: searchResult.isError,
        isFetched: searchResult.isFetched,
        isLoading: searchResult.isLoading || searchResult.isFetching,
        matchCount: searchResult.data?.metadata.result_size.match_count ?? 0,
        numResults: searchResult.data?.metadata.num_results ?? 0,
        searchData: searchResult.data ?? null,
    };
}

function getQueryCacheOptions() {
    // These options combined will make sure that we have referential equality
    // for the current data between renders, as long as the query doesn't
    // change. We need referential equality to ensure that hooks or components
    // using the data as a dependency don't update unless the query has changed.

    const keepPreviousData = <T>(previousData: T) => previousData;

    return {
        // Keep the current data while fetching data for a new query.
        placeholderData: keepPreviousData,
        // Never mark cached data as stale, to avoid refetching data for the
        // current query.
        staleTime: Infinity,
        // Garbage collect cached data immediately, to force refetching data for
        // new queries.
        gcTime: 0,
    };
}

type SearchQueryKey = ReturnType<typeof getSearchQueryKey>;

async function fetchSearchData({
    meta,
    signal,
    queryKey,
}: QueryFunctionContext<SearchQueryKey>): Promise<SearchResult> {
    const { apiUrl } = meta ?? {};
    const [{ searchKey, searchParams }] = queryKey;

    const urlSearchParams = getURLSearchParamsFromQuery(searchParams ?? {});

    // Disable filters for pagination search to improve performance
    // if (urlSearchParams.has(APP_URL_PARAMS.page)) {
    //     urlSearchParams.set('include_filters', 'false');
    //     urlSearchParams.set('include_suggestions', 'false');
    // }

    const url = new URL(`${apiUrl}/search/${searchKey}`);
    url.search = urlSearchParams.toString();

    try {
        const response = await fetch(url, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
            },
            signal,
        });
        if (!response.ok) {
            const message = await response.text();
            return Promise.reject(
                new Error(
                    `Got status ${response.status} calling ${url}: ${message}`,
                ),
            );
        }
        removePreviousSearchQueries(
            meta?.queryClient as QueryClient | undefined,
        );
        return response.json();
    } catch (err) {
        return Promise.reject(
            new Error(`Error calling ${url}`, { cause: err }),
        );
    }
}

function removePreviousSearchQueries(queryClient?: QueryClient) {
    // This is a workaround for removing just the initial server-side rendered
    // search query, and not the subsequent client-side queries. This is
    // necessary because the server-side rendered query is not garbage collected
    // and removed from the cache.

    queryClient?.removeQueries({
        queryKey: getSearchQueryKey(),
        type: 'inactive',
    });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function mergeWithPreviousFilters(
    oldData: SearchResult | undefined,
    newData: SearchResult,
): SearchResult {
    if (!oldData) {
        return newData;
    }

    const isPagingSearch = newData.metadata.paging.current > 1;

    if (isPagingSearch && !deepEqual(oldData, newData)) {
        const sharedData: SearchResult = {
            metadata: {} as SearchResult['metadata'],
        };

        if (oldData.filters) {
            sharedData.filters = oldData.filters;
        }
        if (oldData.metadata.guided_search) {
            sharedData.metadata.guided_search = oldData.metadata.guided_search;
        }
        if (oldData.metadata.selected_filters) {
            sharedData.metadata.selected_filters =
                oldData.metadata.selected_filters;
        }
        if (oldData.metadata.tracking?.object.selectionFilters) {
            sharedData.metadata.tracking = {
                object: {
                    selectionFilters:
                        oldData.metadata.tracking.object.selectionFilters,
                },
            } as SearchResult['metadata']['tracking'];
        }

        return deepMerge(newData, sharedData);
    } else {
        return newData;
    }
}

export function usePageReloadOnSearchError() {
    const { isError, error } = useSearchData();
    const { forcePageReload } = useQueryErrorBoundary();

    React.useEffect(() => {
        if (error && isError) {
            forcePageReload(error);
        }
    }, [error, forcePageReload, isError]);
}
